/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Type,
} from '@angular/core';

import {
    AlertType,
    ILicenseSummary,
    ILicensingStatusResponse,
} from 'ng/modules/licensing/licensing.types';

import {
    map,
    Subject,
    takeUntil,
} from 'rxjs';

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    ICLSStatus,
    ISaasLicensingStatus,
    LicenseTierType,
} from 'generated-types';

import { StateService } from '@uirouter/core';
import { AsyncFactory } from 'ajs/modules/core/factories/async-factory/async.factory';
import { CentralLicenseAlertsService } from 'ng/modules/core/services';
import { L10nService } from '@vmw/ngx-vip';
import { LicenseTierSwitchModalComponent } from 'ng/modules/licensing/components/license-tier-switch-modal';
import { SchemaService } from 'ajs/modules/core/services';
import { statusPollingDuration } from 'ajs/modules/cportal/constants';
import { Store } from '@ngrx/store';
import { SystemConfig } from 'ajs/modules/system/factories/system-config.item.factory';
import * as SystemInfoActions from 'ng/root-store/system-info/system-info.actions';
import { EXTERNAL_CLOUD_SERVICES } from 'ng/modules/core/constants/state-names.constants';

import './licensing-page.component.less';
import { ILicenseAddAlert } from '../license-add-panel/license-add-panel.component';
import { LicensingPageStore } from '../licensing-page.store';
import * as l10n from './licensing-page.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TAsyncFactory = typeof AsyncFactory;

/**
 * License Page Tab Action.
 */
const LICENSING_TAB_ACTIONS = {
    OVERVIEW: 'overview',
    TENANT_USAGE: 'tenant-usage',
};

/**
 * @description Component to display and manage license page.
 * @author Deepak Chaudhary
 */
@Component({
    selector: 'licensing-page',
    templateUrl: './licensing-page.component.html',
})
export class LicensingPageComponent implements OnInit, OnDestroy {
    @Input('loadedSystemConfigService')
    public systemConfig: SystemConfig;

    public readonly saasStatus$ = this.licensingPageStore.saasStatus$;

    public readonly serviceUnits$ = this.licensingPageStore.serviceUnits$;

    public readonly licensePool$ = this.licensingPageStore.licensePool$;

    public readonly licenseList$ = this.licensingPageStore.licenseList$;

    public readonly reservedServiceCores$ = this.licensingPageStore.reservedServiceCores$;

    public readonly availableServiceCores$ = this.licensingPageStore.availableServiceCores$;

    public readonly usedServiceCores$ = this.licensingPageStore.usedServiceCores$;

    public readonly loadingLicenseUsage$ = this.licensingPageStore.loadingLicenseUsage$;

    public readonly loadingLicenses$ = this.licensingPageStore.loadingLicenses$;

    public readonly licenseSummary$ = this.licensingPageStore.licenseSummary$;

    /**
     * Flag to indicate if licensing service is connected (cloud or on-prem).
     */
    public readonly isLicensingServiceConnected$ = this.licensingPageStore.isLicensingServiceConnected$;

    /**
     * Flag to show/hide the add license button.
     */
    public readonly showAddLicense$ = this.licensingPageStore.showAddLicense$;

    /**
     * Legacy license grace period from license status API.
     */
    public readonly legacyLicenseGracePeriod$ = this.licensingPageStore.legacyLicenseGracePeriod$;

    public readonly isOnPremLicensingEnabled$ = this.licensingPageStore.isOnPremLicensingEnabled$;

    public readonly tierChangeInProgress$ = this.licensingPageStore.tierChangeInProgress$;

    /**
     * Observable to determine if license list should be hidden for ENTERPRISE_WITH_CLOUD_SERVICES tier.
     * Hidden when: saasStatus.enabled is true (cloud licensing is enabled).
     */
    public readonly shouldHideLicenseList$ = this.licensingPageStore.saasStatus$.pipe(
        map((saasStatus: ISaasLicensingStatus) => {
            if (this.defaultLicenseTier !== LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES) {
                return false;
            }

            return saasStatus?.enabled;
        }),
    );

    public readonly l10nKeys = l10nKeys;

    public licensingTabActions = LICENSING_TAB_ACTIONS;

    public activeTab = LICENSING_TAB_ACTIONS.OVERVIEW;

    public reserveServiceUnits!: number;

    /**
     * Property to hold license add alert type and message.
     */
    public licenseAddAlert: ILicenseAddAlert | null = null;

    /**
     * Flag to hide license alert on user action.
     */
    public hideLicenseAlert = false;

    public error: any;

    public defaultLicenseTier: LicenseTierType;

    private licenseSummary: ILicenseSummary[] = [];

    private clsStatus: ICLSStatus;

    private connectivityStatus: ALBServicesConnectivityStatus;

    private registrationStatus: ALBServicesRegistrationStatus;

    private saasStatus: ISaasLicensingStatus;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    /**
     * Instance of AsyncFactory when we have an ongoing polling.
     */
    private licensingPollingSubscription: AsyncFactory = null;

    constructor(
        private readonly schemaService: SchemaService,
        private readonly centralLicenseAlertsService: CentralLicenseAlertsService,
        private readonly l10nService: L10nService,
        private readonly licensingPageStore: LicensingPageStore,
        private readonly store: Store,
        private readonly $state: StateService,
        @Inject('AsyncFactory')
        private readonly AsyncFactory: TAsyncFactory,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.licensingPageStore.licenseStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((licenseStatus: ILicensingStatusResponse) => {
                if (licenseStatus) {
                    const { saas_status: saasStatus } = licenseStatus;

                    this.saasStatus = saasStatus;
                    this.reserveServiceUnits = saasStatus?.reserve_service_units ?? 0;
                    this.centralLicenseAlertsService.saasStatus = saasStatus;
                }
            });

        this.licensingPageStore.connectivityStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((connectivityStatus: ALBServicesConnectivityStatus) => {
                this.connectivityStatus = connectivityStatus;
            });

        this.licensingPageStore.registrationStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((registrationStatus: ALBServicesRegistrationStatus) => {
                this.registrationStatus = registrationStatus;
            });

        this.licensingPageStore.clsStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((clsStatus: ICLSStatus) => {
                this.clsStatus = clsStatus;
            });

        this.licensingPageStore.licenseSummary$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((licenseSummary: ILicenseSummary[]) => {
                this.licenseSummary = licenseSummary;
            });

        this.licensingPageStore.tierInUse$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((tierInUse: LicenseTierType) => {
                this.defaultLicenseTier = tierInUse;
            });

        this.licensingPageStore.error$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((storeError: string) => {
                this.error = storeError;
            });
    }

    public get licenseTierTitle(): string {
        return this.schemaService.getEnumValue('LicenseTierType', this.defaultLicenseTier)?.label ?? '';
    }

    /**
     * Return true if ENTERPRISE_WITH_CLOUD_SERVICES tier is selected.
     */
    public get isEnterpriseWithCloudServicesTier(): boolean {
        return this.defaultLicenseTier === LicenseTierType.ENTERPRISE_WITH_CLOUD_SERVICES;
    }

    /**
     * Return true if ENTERPRISE tier is selected.
     */
    public get isEnterpriseTier(): boolean {
        return this.defaultLicenseTier === LicenseTierType.ENTERPRISE;
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.licensingPageStore.clearError();

        this.licensingPageStore.setSelectedLicensingTier(this.systemConfig?.defaultLicenseTier);

        this.licensingPageStore.loadLicenses();
        this.licensingPageStore.loadLicenseUsage(this.defaultLicenseTier, this.reserveServiceUnits);
        this.setupPolling();
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();

        if (this.licensingPollingSubscription) {
            this.licensingPollingSubscription.stop();
        }
    }

    /**
     * Open the license tier change modal.
     */
    public openTierChangeModal(): void {
        this.systemConfig.edit(LicenseTierSwitchModalComponent as Type<Component>, {
            licenseTiersUsageData: this.licenseSummary?.[0]?.tiers,
            onSaveSuccess: async() => {
                this.licensingPageStore.setTierChangeInProgress(true);
                this.store.dispatch(SystemInfoActions.passiveLoadSystemInfo());

                await Promise.all([
                    this.refreshPageData(),
                    this.licensingPageStore.loadLicenseUsage(this.defaultLicenseTier, this.reserveServiceUnits),
                ]);

                this.updateLicensingServiceStatus();
                this.licensingPageStore.setTierChangeInProgress(false);

                if (!this.isEnterpriseWithCloudServicesTier) {
                    this.reserveServiceUnits = 0;
                }
            },
            onOpenCloudServicesModal: () => {
                this.navigateToCloudServices();
            },
        });
    }

    /**
     * Refresh all page data after tier change or modal close.
     */
    public async refreshPageData(): Promise<void> {
        await Promise.all([
            this.licensingPageStore.loadLicenses(),
            this.licensingPageStore.loadLicenseStatus(),
        ]);

        if (this.isEnterpriseWithCloudServicesTier) {
            await this.licensingPageStore.loadConnectivityStatus();
        }
    }

    /**
     * Navigate to Cloud Services page to open the registration modal.
     */
    public navigateToCloudServices(): void {
        this.$state.go(EXTERNAL_CLOUD_SERVICES, { openRegistrationModal: true });
    }

    public handleTabSelect(tab: string): void {
        this.activeTab = tab;
    }

    /**
     * After authentication, check status and show card or open modal.
     */
    public handleConnectToCloudLicensing(eventData: { licensingType: string }): void {
        this.licensingPageStore.setSelectedLicensingTier(eventData?.licensingType as LicenseTierType);
    }

    /**
     * Close license alert message.
     */
    public closeLicenseAlert(): void {
        this.hideLicenseAlert = true;
    }

    /**
     * Called after request for adding a new license is completed.
     * Used to reload license grid if license add request is completed successfully.
     */
    public handleLicenseAdd(licenseAddAlert: ILicenseAddAlert): void {
        this.licenseAddAlert = licenseAddAlert;
        this.hideLicenseAlert = false;

        if (licenseAddAlert.type === AlertType.success) {
            this.reloadLicenseGrid();
        }
    }

    /**
     * Fire after a license is uploaded.
     */
    public reloadLicenseGrid(): void {
        this.licensingPageStore.loadLicenses();
        this.licensingPageStore.loadLicenseUsage(this.defaultLicenseTier, this.reserveServiceUnits);
    }

    /**
     * Handle Use On-prem License Hub button click.
     */
    public handleUseOnPremLicensingService(): void {
        this.licensingPageStore.setSelectedLicensingTier(LicenseTierType.ENTERPRISE);
    }

    /**
     * Handle refresh licenses button click for ENTERPRISE tier.
     */
    public async handleRefreshLicenses(): Promise<void> {
        try {
            await this.licensingPageStore.refreshClsLicenses();
            this.reloadLicenseGrid();
        } catch (error) {
            const errorObj = error as { error?: string };

            this.error = errorObj?.error ||
                this.l10nService.getMessage(l10nKeys.failedToRefreshLicensesError);
        }
    }

    /**
     * Handle error from child component.
     */
    public handleStatusCardError(errorMessage: string): void {
        this.error = errorMessage;
    }

    public get licenseStatusCardTitle(): string {
        return this.defaultLicenseTier === LicenseTierType.ENTERPRISE ?
            this.l10nService.getMessage(l10nKeys.onPremLicensingServiceLabel) :
            this.l10nService.getMessage(l10nKeys.cloudLicensingLabel);
    }

    /**
     * Stop polling for alb-services/status and licensing/status APIs.
     */
    private stopPolling(): void {
        if (this.licensingPollingSubscription) {
            this.licensingPollingSubscription.stop();
            this.licensingPollingSubscription = null;
        }
    }

    private updateLicensingServiceStatus(): void {
        let status = false;

        if (this.isEnterpriseTier) {
            status = this.clsStatus?.enabled;
        } else if (this.isEnterpriseWithCloudServicesTier) {
            const isConnected = this.connectivityStatus === ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED;
            const cloudConnectedNotEnabled = !this.saasStatus?.enabled && isConnected;

            status = this.saasStatus?.enabled || cloudConnectedNotEnabled;
        }

        this.licensingPageStore.setLicensingServiceConnected(status);
    }

    /**
     * Setup polling for alb-services/status and licensing/status APIs.
     */
    private setupPolling(): void {
        this.stopPolling();

        this.licensingPollingSubscription = new this.AsyncFactory(async() => {
            if (this.isEnterpriseWithCloudServicesTier) {
                await this.licensingPageStore.loadConnectivityStatus();
            }

            await this.licensingPageStore.loadLicenseStatus();
            this.updateLicensingServiceStatus();
        }, {
            maxSkipped: 5,
            callback: () => this.store.dispatch(SystemInfoActions.cancelLoading()),
        });

        this.licensingPollingSubscription.start(statusPollingDuration);
    }
}
